<!-- 工坊面板（制造地点化 v1）：到达即用 → 选配方开单 → 计时产出 → 回坊领取 -->
<template>
  <div id="workshopDiv" class="wrap">
    <el-dialog :visible.sync="workshopVisible" :before-close="dialog_close" append-to-body
               :title="title" :close-on-click-modal="false" width="640px" v-loading="loading">
      <div v-if="detail && detail.facility">
        <!-- 场地状态 -->
        <div class="venue-banner" :class="bannerClass">
          <template v-if="detail.facility.status !== 'active'">这间工坊还没开张，等匠人把炉子支起来吧。</template>
          <template v-else-if="detail.tip">{{ detail.tip }}</template>
          <template v-else>✅ 你正在【{{ detail.facility.facilityName }}】里{{ detail.facility.mapName ? '（' + detail.facility.mapName + '）' : '' }}。</template>
        </div>

        <!-- 我的这单 -->
        <div class="my-order" v-if="detail.myOrder">
          <div class="order-line">
            <span class="order-item">{{ detail.myOrder.itemName }}</span>
            <span v-if="orderCooking" class="order-state">
              {{ isAlchemy ? '🧪 炼制中' : '🔨 打造中' }} · 还剩 <b>{{ remainText }}</b>
            </span>
            <span v-else class="order-state ready">📦 已做好，等你领取</span>
          </div>
          <div class="order-tip">每间工坊同时只能开一单——领走才能开下一件。</div>
          <!-- 到点就给按钮：状态翻转由服务端 done，前端不能只认 claimable，否则倒计时归零后无按钮可点 -->
          <el-button size="small" type="success" v-if="canClaim" @click="doClaim">领取</el-button>
        </div>

        <!-- 药剂（药庐）：投料制——从背包挑实例道具，不是选配方 -->
        <div class="recipe-area" v-if="isAlchemy">
          <div class="section-title">
            投料合成
            <span class="section-sub" v-if="detail.alchemy">
              熟练度 {{ detail.alchemy.mastery || 0 }} · 单炉约 {{ fmtTime(alchemySeconds) }}
            </span>
          </div>
          <div class="alchemy-rules">
            <div>· 恰选 <b>3 件</b>：<b>{{ seedName }} + 2 种花</b> → 神秘之尘或大力丸。</div>
            <div>· 恰选 <b>4 件</b>：<b>{{ seedName }} + 2 种花 + 1 条{{ catalystName }}</b> → 指定属性胶囊（属性 +1）。</div>
            <div>· 4 件路径需<b>体力全满、疲劳为 0</b>才能开炉。</div>
            <div class="alchemy-hint">不同花种组合对应不同属性胶囊，配方需自行摸索；花由花匠打造。</div>
          </div>
          <div class="fee-tip" v-if="feeRateText">
            每炉按<b>材料价值抽 {{ feeRateText }}%</b> 手续费。
          </div>
          <div class="recipe-grid" v-if="alchemyItems.length">
            <div class="recipe-card" v-for="it in alchemyItems" :key="it.itemId"
                 :class="{active: isPicked(it.itemId)}" @click="togglePick(it)">
              <div class="recipe-name">{{ it.itemName }}<span v-if="it.stackCount > 1" class="stack-x">×{{ it.stackCount }}</span></div>
              <div class="recipe-meta" v-if="it.seed">种子 · 每炉 1 片</div>
              <div class="recipe-meta" v-else-if="it.catalyst">触媒 · 最多 1 条</div>
              <div class="recipe-meta" v-else-if="it.score != null && it.score > -1">评分 {{ it.score }}</div>
              <div class="recipe-time">{{ isPicked(it.itemId) ? '已选' : '点击选中' }}</div>
            </div>
          </div>
          <div class="empty-tip" v-else>背包里没有可投料的道具——先去采点花、钓条鱼。</div>
        </div>

        <!-- 可制造清单 -->
        <div class="recipe-area" v-else>
          <div class="section-title">
            可制造清单
            <span class="section-sub" v-if="detail.userAbility">
              熟练度 {{ detail.userAbility.mastery || 0 }} · 工坊内涨熟练 +{{ detail.masteryBonusPct }}% 概率
            </span>
          </div>
          <!-- 制造手续费明示（2026-10-21）：按材料价值抽成，费率由服务端下发，不会与实收漂移 -->
          <div class="fee-tip" v-if="feeRateText">
            每次开工会按<b>材料价值抽 {{ feeRateText }}%</b> 手续费（做一件扣一次）。
          </div>
          <div class="recipe-grid" v-if="recipes.length">
            <div class="recipe-card" v-for="r in recipes" :key="r.recipe.id"
                 :class="{locked: r.locked, active: selected === r.recipe.id}"
                 @click="select(r)">
              <div class="recipe-name">{{ r.recipe.name }}</div>
              <div class="recipe-meta">需熟练 {{ r.recipe.mastery }}</div>
              <div class="recipe-mat">{{ materialText(r.recipe) }}</div>
              <div class="recipe-time">≈ {{ fmtTime(r.craftSeconds) }}</div>
            </div>
          </div>
          <div class="empty-tip" v-else>这间工坊暂时没有你能做的东西。</div>
        </div>

        <!-- 开单（打造） -->
        <div class="start-area" v-if="!isAlchemy">
          <span class="start-hint" v-if="selectedRecipe">
            已选【{{ selectedRecipe.name }}】：消耗体力 50 / 疲劳 5，约 {{ fmtTime(selectedSeconds) }} 后完成<template v-if="feeRateText">，另付 {{ feeRateText }}% 手续费</template>。
          </span>
          <span class="start-hint" v-else>点一个配方再开单。</span>
          <el-button type="primary" :disabled="!canStart" @click="doStart">开 工</el-button>
        </div>

        <!-- 投料（药庐） -->
        <div class="start-area" v-if="isAlchemy">
          <span class="start-hint" :class="{'hint-bad': !canAlchemy}">
            已选 {{ pickedItems.length }} 件{{ alchemyHint }}
          </span>
          <el-button type="primary" :disabled="!canAlchemy" @click="doAlchemy">投 料</el-button>
        </div>
      </div>
      <div v-else-if="loadError" class="empty-tip">
        {{ loadError }}
        <div style="margin-top:10px">
          <el-button size="small" type="primary" plain @click="loadDetail">重试</el-button>
        </div>
      </div>
      <div v-else class="empty-tip">工坊信息加载中……</div>
    </el-dialog>
  </div>
</template>

<script>
import {postRequest} from '../../utils/api'
import Message from 'element-ui/lib/message'

export default {
  name: 'workshopPanel',
  props: {
    workshopVisible: {
      type: Boolean,
      default: false
    },
    facilityKey: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      detail: null,
      loadError: '',
      selected: null,
      pickedItems: [],
      remain: 0,
      zeroTicks: 0,
      timer: null
    };
  },
  computed: {
    title: function () {
      return this.detail && this.detail.facility ? this.detail.facility.facilityName : '工坊';
    },
    bannerClass: function () {
      if (!this.detail) return '';
      if (this.detail.facility.status !== 'active') return 'warn';
      return this.detail.tip ? 'warn' : 'ok';
    },
    recipes: function () {
      return this.detail && this.detail.recipes ? this.detail.recipes : [];
    },
    selectedRecipe: function () {
      for (var i = 0; i < this.recipes.length; i++) {
        if (this.recipes[i].recipe.id === this.selected) {
          return this.recipes[i].recipe;
        }
      }
      return null;
    },
    selectedSeconds: function () {
      for (var i = 0; i < this.recipes.length; i++) {
        if (this.recipes[i].recipe.id === this.selected) {
          return this.recipes[i].craftSeconds;
        }
      }
      return 0;
    },
    canStart: function () {
      if (!this.selectedRecipe) return false;
      if (!this.detail || this.detail.facility.status !== 'active') return false;
      if (this.detail.tip) return false;
      if (this.detail.myOrder) return false;
      return true;
    },
    remainText: function () {
      return this.fmtTime(this.remain);
    },
    // 还在倒计时（且真的没到点）
    orderCooking: function () {
      var o = this.detail && this.detail.myOrder;
      if (!o) return false;
      if (o.status !== 'cooking') return false;
      if (this.remain > 0) return true;
      // 服务端也报 0 秒却仍是 cooking → 视为已到点（翻转延迟），按「已做好」展示
      return (o.remainingSeconds || 0) > 0;
    },
    // 可领取：服务端已翻 claim，或本地/服务端倒计时都归零（服务端 claim 会自行翻转，不必先 claimable）
    canClaim: function () {
      var o = this.detail && this.detail.myOrder;
      if (!o) return false;
      if (o.claimable) return true;
      return o.status === 'cooking' && this.remain <= 0 && (o.remainingSeconds || 0) <= 0;
    },
    // 制造手续费率（2026-10-21）：服务端下发千分比，转成百分比文案；0/缺失=不显示
    feeRateText: function () {
      var d = this.detail;
      if (!d) return '';
      var rate = Number(d.craftFeeRate);
      if (!rate || isNaN(rate) || rate <= 0) return '';
      // 50‰ → "5"；避免 4.999999 这类浮点尾巴
      return String(Math.round(rate / 10 * 10) / 10);
    },
    // ==================== 药剂（药庐，2026-10-22） ====================
    // 药庐的材料是背包实例投料，服务端返回 mode=alchemy 时整块换成投料界面
    isAlchemy: function () {
      return !!this.detail && this.detail.mode === 'alchemy';
    },
    alchemyItems: function () {
      return this.detail && this.detail.alchemy && this.detail.alchemy.items
        ? this.detail.alchemy.items : [];
    },
    alchemySeconds: function () {
      return this.detail && this.detail.alchemy ? (this.detail.alchemy.seconds || 60) : 60;
    },
    seedName: function () {
      return this.detail && this.detail.alchemy ? this.detail.alchemy.seedName : '神秘药片';
    },
    catalystName: function () {
      return this.detail && this.detail.alchemy ? this.detail.alchemy.catalystName : '金龙鱼';
    },
    pickedSeedCount: function () {
      return this.countPicked('seed');
    },
    pickedCatalystCount: function () {
      return this.countPicked('catalyst');
    },
    // 投料规则与服务端 startAlchemy 的校验一一对应，前端先拦一道避免白跑
    canAlchemy: function () {
      if (!this.detail || this.detail.facility.status !== 'active') return false;
      if (this.detail.tip) return false;
      if (this.detail.myOrder) return false;
      if (!this.detail.userAbility) return false;
      var n = this.pickedItems.length;
      if (n !== 3 && n !== 4) return false;
      if (this.pickedSeedCount !== 1) return false;
      if (this.pickedCatalystCount > 1) return false;
      if (this.pickedCatalystCount === 1 && n !== 4) return false;
      return true;
    },
    alchemyHint: function () {
      var n = this.pickedItems.length;
      if (this.pickedSeedCount !== 1) return '（必须有且只有 1 片' + this.seedName + '）';
      if (this.pickedCatalystCount > 0) {
        return n === 4 ? '（含 1 条' + this.catalystName + '，可出属性胶囊）' : '（放' + this.catalystName + '就是 4 件）';
      }
      if (n === 3) return '（药片 + 2 种花）';
      if (n === 4) return '（4 件但没有' + this.catalystName + '，只会出大力丸线）';
      return '（需 3 件：药片 + 2 种花；或 4 件：再加 1 条' + this.catalystName + '）';
    }
  },
  watch: {
    workshopVisible: {
      // immediate：组件由 showWorkshopDiv v-if 首挂时 workshopVisible 已为 true，
      // 普通 watch 只在"变化"时触发、首挂不执行 → detail 永不加载（表现为一直「工坊信息加载中」）
      immediate: true,
      handler(val) {
        if (val) {
          this.selected = null;
          this.pickedItems = [];
          this.loadDetail();
          this.startTimer();
        } else {
          this.stopTimer();
        }
      }
    }
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    dialog_close() {
      this.$emit('workshop_dialog_close');
    },
    loadDetail() {
      var _this = this;
      if (!_this.facilityKey) {
        _this.loadError = '没拿到工坊标识，请重开一次。';
        return;
      }
      _this.loading = true;
      _this.loadError = '';
      postRequest('/user/workshop/detail', {facilityKey: _this.facilityKey}).then(resp => {
        _this.loading = false;
        if (resp.status === 200 && resp.data && resp.data.status === 200) {
          _this.detail = resp.data.data;
          var order = _this.detail.myOrder;
          if (order && order.status === 'cooking') {
            _this.remain = order.remainingSeconds || 0;
          } else {
            _this.remain = 0;
          }
        } else {
          _this.loadError = resp.data && resp.data.message ? resp.data.message : '工坊信息加载失败';
          Message.error(_this.loadError);
        }
      }, () => {
        _this.loading = false;
        _this.loadError = '网络异常，请稍后再试';
        Message.error(_this.loadError);
      });
    },
    startTimer() {
      var _this = this;
      _this.stopTimer();
      _this.zeroTicks = 0;
      _this.timer = setInterval(function () {
        var order = _this.detail && _this.detail.myOrder;
        if (!order || order.status !== 'cooking') return;
        if (_this.remain > 0) {
          _this.remain -= 1;
          if (_this.remain <= 0) {
            // 到点：回后端确认翻 claim（离线也照常计时，所以以服务端为准）
            _this.zeroTicks = 0;
            _this.loadDetail();
          }
          return;
        }
        // 已归零但服务端还没翻成 claim：每 5 秒回查一次，别把面板卡死在「还剩 0 秒」
        _this.zeroTicks = (_this.zeroTicks || 0) + 1;
        if (_this.zeroTicks % 5 === 0 && _this.zeroTicks > 0) {
          _this.loadDetail();
        }
      }, 1000);
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    select(r) {
      if (r.locked) {
        Message.warning('熟练度还不够——先把熟练度练到 ' + r.recipe.mastery);
        return;
      }
      this.selected = r.recipe.id;
    },
    doStart() {
      var _this = this;
      if (!_this.selectedRecipe) return;
      _this.$confirm('开单会先扣材料和体力' + (_this.feeRateText ? '，并按材料价值收取 ' + _this.feeRateText + '% 手续费' : '') + '，' + _this.fmtTime(_this.selectedSeconds) + '后回来领取。确定开工吗？', '提示', {
        confirmButtonText: '开工',
        cancelButtonText: '再想想',
        type: 'warning'
      }).then(function () {
        _this.loading = true;
        postRequest('/user/workshop/start', {
          facilityKey: _this.facilityKey,
          createId: _this.selectedRecipe.id,
          userAbilityId: _this.detail.userAbility.id
        }).then(resp => {
          _this.loading = false;
          if (resp.status === 200 && resp.data && resp.data.status === 200) {
            Message.success(resp.data.message);
            // 制造手续费实收金额（2026-10-21）：后端已把金额并入 message，这里再单独提示一次
            var fee = resp.data.data ? Number(resp.data.data.fee) : 0;
            if (fee > 0) {
              Message.warning('本次手续费 ' + fee + ' 元');
            }
            _this.selected = null;
            _this.$store.dispatch('user/fetchUserDetail');
            _this.loadDetail();
          } else {
            Message.error(resp.data ? resp.data.message : '开工失败');
          }
        }, () => {
          _this.loading = false;
          Message.error('网络异常，请稍后再试');
        });
      }, function () {
      });
    },
    doClaim() {
      var _this = this;
      var order = _this.detail && _this.detail.myOrder;
      if (!order) return;
      _this.loading = true;
      postRequest('/user/workshop/claim', {
        facilityKey: _this.facilityKey,
        orderId: order.orderId
      }).then(resp => {
        _this.loading = false;
        if (resp.status === 200 && resp.data && resp.data.status === 200) {
          Message.success(resp.data.message);
          _this.loadDetail();
        } else {
          Message.error(resp.data ? resp.data.message : '领取失败');
        }
      }, () => {
        _this.loading = false;
        Message.error('网络异常，请稍后再试');
      });
    },
    // 过滤已选列表里某类标记的件数（seed=药片，catalyst=金龙鱼）
    countPicked(flag) {
      var n = 0;
      for (var i = 0; i < this.pickedItems.length; i++) {
        if (this.pickedItems[i][flag]) n++;
      }
      return n;
    },
    isPicked(itemId) {
      for (var i = 0; i < this.pickedItems.length; i++) {
        if (this.pickedItems[i].itemId === itemId) return true;
      }
      return false;
    },
    togglePick(item) {
      for (var i = 0; i < this.pickedItems.length; i++) {
        if (this.pickedItems[i].itemId === item.itemId) {
          this.pickedItems.splice(i, 1);
          return;
        }
      }
      if (this.pickedItems.length >= 4) {
        Message.warning('一炉最多投 4 件（药片 + 2 种花 + 1 条金龙鱼）');
        return;
      }
      // 药片/金龙鱼各只能 1 件：重复点就替换，避免选了 2 片药片还看不出问题
      if (item.seed || item.catalyst) {
        for (var j = this.pickedItems.length - 1; j >= 0; j--) {
          if ((item.seed && this.pickedItems[j].seed) || (item.catalyst && this.pickedItems[j].catalyst)) {
            this.pickedItems.splice(j, 1);
          }
        }
      }
      this.pickedItems.push(item);
    },
    doAlchemy() {
      var _this = this;
      if (!_this.canAlchemy) return;
      var ids = [];
      for (var i = 0; i < _this.pickedItems.length; i++) {
        ids.push(_this.pickedItems[i].itemId);
      }
      _this.$confirm('投料后材料立刻被消耗' + (_this.feeRateText ? '，并按材料价值收取 ' + _this.feeRateText + '% 手续费' : '')
        + '，' + _this.fmtTime(_this.alchemySeconds) + '后回药庐揭盖。确定投料吗？', '提示', {
        confirmButtonText: '投料',
        cancelButtonText: '再想想',
        type: 'warning'
      }).then(function () {
        _this.loading = true;
        postRequest('/user/workshop/alchemy', {
          facilityKey: _this.facilityKey,
          itemIds: ids.join(','),
          userAbilityId: _this.detail.userAbility.id
        }).then(resp => {
          _this.loading = false;
          if (resp.status === 200 && resp.data && resp.data.status === 200) {
            Message.success(resp.data.message);
            var fee = resp.data.data ? Number(resp.data.data.fee) : 0;
            if (fee > 0) {
              Message.warning('本次手续费 ' + fee + ' 元');
            }
            _this.pickedItems = [];
            _this.$store.dispatch('user/fetchUserDetail');
            _this.loadDetail();
          } else {
            Message.error(resp.data ? resp.data.message : '投料失败');
          }
        }, () => {
          _this.loading = false;
          Message.error('网络异常，请稍后再试');
        });
      }, function () {
      });
    },
    materialText(recipe) {
      if (!recipe || !recipe.itemMaterials || !recipe.itemMaterials.length) {
        return '无需材料';
      }
      var parts = [];
      for (var i = 0; i < recipe.itemMaterials.length; i++) {
        var m = recipe.itemMaterials[i];
        parts.push(m.name + '×' + m.count);
      }
      return parts.join('、');
    },
    fmtTime(seconds) {
      var s = Number(seconds) || 0;
      if (s >= 60) {
        var m = Math.floor(s / 60);
        var rest = s % 60;
        return rest > 0 ? (m + '分' + rest + '秒') : (m + '分钟');
      }
      return s + '秒';
    }
  }
}
</script>

<style scoped>
.venue-banner {
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 10px;
  font-size: 14px;
}
.venue-banner.ok {
  background: #f0f9eb;
  color: #529b2e;
}
.venue-banner.warn {
  background: #fdf6ec;
  color: #b88230;
}
.my-order {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 12px;
  background: #fafafa;
}
.order-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
}
.order-item {
  font-weight: bold;
}
.order-state.ready {
  color: #529b2e;
  font-weight: bold;
}
.order-tip {
  margin: 6px 0 8px;
  font-size: 12px;
  color: #909399;
}
.section-title {
  font-weight: bold;
  margin-bottom: 8px;
}
.section-sub {
  font-weight: normal;
  font-size: 12px;
  color: #909399;
  margin-left: 8px;
}
/* 制造手续费明示（2026-10-21） */
.fee-tip {
  margin: 6px 0 8px;
  padding: 6px 10px;
  font-size: 12px;
  color: #e6a23c;
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  border-radius: 4px;
}
.recipe-grid {
  display: flex;
  flex-wrap: wrap;  gap: 8px;
  max-height: 260px;
  overflow-y: auto;
}
.recipe-card {
  width: 138px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  font-size: 12px;
  line-height: 1.5;
}
.recipe-card.active {
  border-color: #409eff;
  background: #ecf5ff;
}
.recipe-card.locked {
  opacity: .45;
  cursor: not-allowed;
}
.recipe-name {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
}
.recipe-meta {
  color: #909399;
}
.recipe-mat {
  color: #606266;
  word-break: break-all;
}
.recipe-time {
  color: #e6a23c;
}
.start-area {
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.start-hint {
  font-size: 12px;
  color: #909399;
}
.empty-tip {
  text-align: center;
  color: #909399;
  padding: 20px 0;
}
/* 药剂（药庐，2026-10-22）：投料规则与已选提示 */
.alchemy-rules {
  font-size: 12px;
  line-height: 1.7;
  color: #606266;
  background: #f4f8fb;
  border: 1px solid #d9ecff;
  border-radius: 4px;
  padding: 8px 10px;
  margin-bottom: 8px;
}
.alchemy-hint {
  color: #909399;
}
.stack-x {
  font-size: 11px;
  color: #909399;
  margin-left: 2px;
}
.hint-bad {
  color: #f56c6c;
}
</style>
